import { queryOptions, skipToken } from "@tanstack/react-query";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import {
  getCase,
  getChildCasesByMaintenance,
  getPaginatedCases,
  getPaginatedCasesArchived,
  getPaginatedCasesOpen,
  getPaginatedCasesResolved,
  getParentCaseByMaintenance,
} from "@/api/resources/cases/api";
import { transformPaginatedCases } from "@/api/resources/cases/selectors";
import i18n from "@/providers/i18n";
import { stringifyQueryPaginationFilters, type QueryPaginationFilters } from "@/utils/pagination";

const entity = i18n.t("case");
const entityErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entity }),
};
const entities = i18n.t("cases");
const entitiesErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entities }),
};

export const casesQueryKeys = {
  all: () => [...MASTER_INVALIDATION_KEY, "cases"],
  lists: () => [...casesQueryKeys.all(), "list"],
  listChildCasesByMaintenance: (maintenance_id?: string) => [
    ...casesQueryKeys.lists(),
    "childCasesByMaintenance",
    maintenance_id,
  ],
  parentCaseByMaintenance: (maintenance_id?: string) => [
    ...casesQueryKeys.lists(),
    "parentCaseByMaintenance",
    maintenance_id,
  ],
  listPaginated: (queryKeyPaginationFilters: QueryPaginationFilters) => [
    ...casesQueryKeys.lists(),
    "paginated",
    stringifyQueryPaginationFilters(queryKeyPaginationFilters),
  ],
  listOpen: (queryKeyPaginationFilters: QueryPaginationFilters) => [
    ...casesQueryKeys.lists(),
    "open",
    stringifyQueryPaginationFilters(queryKeyPaginationFilters),
  ],
  listResolved: (queryKeyPaginationFilters: QueryPaginationFilters) => [
    ...casesQueryKeys.lists(),
    "resolved",
    stringifyQueryPaginationFilters(queryKeyPaginationFilters),
  ],
  listArchived: (queryKeyPaginationFilters: QueryPaginationFilters) => [
    ...casesQueryKeys.lists(),
    "archived",
    stringifyQueryPaginationFilters(queryKeyPaginationFilters),
  ],
  byId: (id?: string) => [...casesQueryKeys.all(), "byId", id],
};

export const casesQueries = {
  listChildCasesByMaintenance: (maintenance_id?: string) =>
    queryOptions({
      queryKey: [...casesQueryKeys.listChildCasesByMaintenance(maintenance_id)],
      queryFn: maintenance_id ? () => getChildCasesByMaintenance(maintenance_id) : skipToken,

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  parentCaseByMaintenance: (maintenance_id?: string) =>
    queryOptions({
      queryKey: [...casesQueryKeys.parentCaseByMaintenance(maintenance_id)],
      queryFn: maintenance_id ? () => getParentCaseByMaintenance(maintenance_id) : skipToken,

      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
  listPaginated: (queryKeyPaginationFilters: QueryPaginationFilters) =>
    queryOptions({
      queryKey: [...casesQueryKeys.listPaginated(queryKeyPaginationFilters)],
      queryFn: () => getPaginatedCases(queryKeyPaginationFilters),

      meta: {
        errorMessage: entitiesErrorMessage,
      },
      select: ({ data, count }) => ({ count, data: transformPaginatedCases(data) }),
    }),
  listOpen: (queryKeyPaginationFilters: QueryPaginationFilters) =>
    queryOptions({
      queryKey: [...casesQueryKeys.listOpen(queryKeyPaginationFilters)],
      queryFn: () => getPaginatedCasesOpen(queryKeyPaginationFilters),

      meta: {
        errorMessage: entitiesErrorMessage,
      },
      select: ({ data, count }) => ({ count, data: transformPaginatedCases(data) }),
    }),
  listResolved: (queryKeyPaginationFilters: QueryPaginationFilters) =>
    queryOptions({
      queryKey: [...casesQueryKeys.listResolved(queryKeyPaginationFilters)],
      queryFn: () => getPaginatedCasesResolved(queryKeyPaginationFilters),

      meta: {
        errorMessage: entitiesErrorMessage,
      },
      select: ({ data, count }) => ({ count, data: transformPaginatedCases(data) }),
    }),
  listArchived: (queryKeyPaginationFilters: QueryPaginationFilters) =>
    queryOptions({
      queryKey: [...casesQueryKeys.listArchived(queryKeyPaginationFilters)],
      queryFn: () => getPaginatedCasesArchived(queryKeyPaginationFilters),

      meta: {
        errorMessage: entitiesErrorMessage,
      },
      select: ({ data, count }) => ({ count, data: transformPaginatedCases(data) }),
    }),
  byId: (id?: string) =>
    queryOptions({
      queryKey: [...casesQueryKeys.byId(id)],
      queryFn: id ? () => getCase({ match: { id } }) : skipToken,

      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
};
